import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Phone, MessageCircle, CalendarPlus, CalendarClock, NotebookPen, Share2, Pencil, BadgeCheck, Trash2, AlertTriangle, ChevronRight, ChevronDown,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import QuickNoteButton from "@/components/QuickNoteButton";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import RecordTasksTab from "@/components/RecordTasksTab";
import { BrokerMeetingsTab } from "@/components/meetings/MeetingViews";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatDate, formatINR } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useMasterList } from "@/hooks/useMasterData";
import MasterSelect from "@/components/brokers/MasterSelect";
import BrokerFormFields from "@/components/brokers/BrokerFormFields";
import ShareAssetDialog from "@/components/brokers/ShareAssetDialog";
import TonalBadge from "@/components/TonalBadge";

function LogActivityDialog({ open, onClose, brokerId, shares }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = useMutation({
    mutationFn: async () => api.post(`/brokers/${brokerId}/activities`, {
      related_share_id: form.related_share_id || null,
      activity_type_id: form.activity_type_id,
      comments: form.comments || null,
      outcome_id: form.outcome_id || null,
      next_followup_date: form.next_followup_date ? new Date(form.next_followup_date).toISOString() : null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["broker-activities", brokerId] });
      qc.invalidateQueries({ queryKey: ["broker", brokerId] });
      qc.invalidateQueries({ queryKey: ["broker-shares", brokerId] });
      toast.success("Activity logged");
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="log-activity-dialog">
        <DialogHeader><DialogTitle>Log Activity</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Activity Type *</Label>
            <div className="mt-1"><MasterSelect listType="activity_type" value={form.activity_type_id} onChange={(v) => set("activity_type_id", v)} placeholder="Type…" testId="activity-type" /></div>
          </div>
          <div>
            <Label>Related Share (optional)</Label>
            <Select value={form.related_share_id || "none"} onValueChange={(v) => set("related_share_id", v === "none" ? "" : v)}>
              <SelectTrigger className="mt-1" data-testid="activity-share"><SelectValue placeholder="General relationship" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">General relationship</SelectItem>
                {(shares || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.asset_name} ({s.share_status_label})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Comments</Label>
            <Textarea value={form.comments || ""} onChange={(e) => set("comments", e.target.value)} className="mt-1" rows={2} data-testid="activity-comments" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Outcome</Label>
              <div className="mt-1"><MasterSelect listType="activity_outcome" value={form.outcome_id} onChange={(v) => set("outcome_id", v)} placeholder="Outcome…" testId="activity-outcome" /></div>
            </div>
            <div>
              <Label>Next Follow-up</Label>
              <Input type="datetime-local" value={form.next_followup_date || ""} onChange={(e) => set("next_followup_date", e.target.value)} className="mt-1" data-testid="activity-next-followup" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} disabled={!form.activity_type_id || save.isPending} className="bg-slate-900 hover:bg-slate-800" data-testid="activity-submit">Log</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditBrokerDialog({ open, onClose, broker }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(broker);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = useMutation({
    mutationFn: async () => api.patch(`/brokers/${broker.id}`, form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["broker", broker.id] });
      toast.success("Saved");
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="edit-broker-dialog">
        <DialogHeader><DialogTitle>Edit Broker</DialogTitle></DialogHeader>
        <BrokerFormFields form={form} set={set} />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} className="bg-slate-900 hover:bg-slate-800" data-testid="edit-broker-submit">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteBrokerDialog({ open, onClose, broker, onDeleted }) {
  const [step, setStep] = useState(2);
  const [confirmText, setConfirmText] = useState("");
  const [search, setSearch] = useState("");
  const [replacement, setReplacement] = useState(null); // {id, broker_name, firm_name, broker_type_label}

  const { data: preview } = useQuery({
    queryKey: ["broker-delete-preview", broker.id],
    queryFn: async () => (await api.get(`/brokers/${broker.id}/delete-preview`)).data,
    enabled: open,
  });
  const hasActiveDeals = !!preview?.has_active_deals;
  const dealCount = preview?.deal_count ?? 0;

  // Active brokers for reassignment (exclude self, Inactive, Blacklisted).
  const { data: allBrokers = [] } = useQuery({
    queryKey: ["brokers-for-reassign"],
    queryFn: async () => (await api.get(`/brokers`)).data,
    enabled: open && hasActiveDeals,
  });
  const eligible = useMemo(() => allBrokers.filter((b) =>
    b.id !== broker.id &&
    !["Inactive", "Blacklisted"].includes(b.relationship_status_label)
  ), [allBrokers, broker.id]);
  const noReplacement = hasActiveDeals && allBrokers.length > 0 && eligible.length === 0;
  const results = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 2) return [];
    return eligible.filter((b) =>
      (b.broker_name || "").toLowerCase().includes(q) ||
      (b.firm_name || "").toLowerCase().includes(q)
    ).slice(0, 20);
  }, [search, eligible]);

  // Reset + decide starting step whenever the dialog opens / preview resolves.
  useEffect(() => {
    if (!open) return;
    setConfirmText(""); setSearch(""); setReplacement(null);
  }, [open]);
  useEffect(() => {
    if (!open || !preview) return;
    setStep(hasActiveDeals ? 1 : 2);
  }, [open, preview, hasActiveDeals]);

  const del = useMutation({
    mutationFn: async () => (await api.delete(`/brokers/${broker.id}`, {
      params: replacement ? { reassign_to_broker_id: replacement.id } : {},
    })).data,
    onSuccess: () => { toast.success(`${broker.broker_name} deleted`); onClose(); onDeleted(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to delete broker"),
  });

  const nameMatches = confirmText.trim().toLowerCase() === (broker.broker_name || "").trim().toLowerCase();
  const c = preview?.counts || {};
  const multiStep = hasActiveDeals;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="delete-broker-dialog">
        {step === 1 ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="h-5 w-5" />Active Deals Found — Reassign Before Deleting
              </DialogTitle>
              <span className="text-xs text-slate-500 font-medium" data-testid="delete-step-indicator">Step 1 of 2</span>
            </DialogHeader>
            {noReplacement ? (
              <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="delete-no-replacement-msg">
                No active brokers available to reassign deals to. Please add another broker before deleting this one.
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-700">
                  <span className="font-semibold">{broker.broker_name}</span> has <span className="font-semibold">{dealCount}</span> active deal commission record{dealCount === 1 ? "" : "s"}. Choose another active broker to reassign {dealCount === 1 ? "it" : "them"} to before deleting.
                </p>
                <div>
                  <Label className="text-xs">Search active brokers (name or company)</Label>
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Type at least 2 characters…" className="mt-1" data-testid="reassign-search-input" autoFocus={false} />
                </div>
                {search.trim().length >= 2 && (
                  <div className="border rounded-md divide-y max-h-52 overflow-y-auto" data-testid="reassign-results">
                    {results.length === 0 ? (
                      <div className="p-3 text-sm text-slate-500">No matching active brokers.</div>
                    ) : results.map((b) => (
                      <button
                        key={b.id}
                        type="button"
                        onClick={() => setReplacement(b)}
                        className={`w-full text-left p-2.5 hover:bg-slate-50 transition-colors ${replacement?.id === b.id ? "bg-slate-100" : ""}`}
                        data-testid={`reassign-option-${b.id}`}
                      >
                        <div className="text-sm font-medium text-slate-800">{b.broker_name}</div>
                        <div className="text-xs text-slate-500">{[b.firm_name, b.broker_type_label].filter(Boolean).join(" · ") || "—"}</div>
                      </button>
                    ))}
                  </div>
                )}
                {replacement && (
                  <div className="text-sm bg-slate-50 border border-slate-200 rounded-md p-2.5" data-testid="reassign-selected">
                    Reassign to: <span className="font-semibold">{replacement.broker_name}</span>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={onClose} data-testid="delete-cancel-btn">Cancel</Button>
              {!noReplacement && (
                <Button className="bg-slate-900 hover:bg-slate-800 text-white" disabled={!replacement} onClick={() => setStep(2)} data-testid="reassign-continue-btn">
                  Continue
                </Button>
              )}
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="h-5 w-5" />Permanently Delete {broker.broker_name}?
              </DialogTitle>
              {multiStep && <span className="text-xs text-slate-500 font-medium" data-testid="delete-step-indicator">Step 2 of 2</span>}
            </DialogHeader>
            <div className="space-y-3">
              {replacement && (
                <div className="text-sm bg-emerald-50 border border-emerald-200 rounded-md p-2.5 text-emerald-800" data-testid="delete-reassign-summary">
                  Deals will be reassigned to: <span className="font-semibold">{replacement.broker_name}</span>
                </div>
              )}
              <p className="text-sm text-slate-700">This will <span className="font-semibold">permanently remove</span> this broker and all associated records:</p>
              <ul className="text-sm text-slate-700 list-disc pl-5 space-y-0.5" data-testid="delete-counts">
                {hasActiveDeals && <li>Deals: {dealCount} (will be reassigned to {replacement?.broker_name || "—"})</li>}
                <li>Shared Assets: {c.shared_assets ?? 0}</li>
                <li>Meeting Logs: {c.meeting_logs ?? 0}</li>
                <li>Activities: {c.activities ?? 0}</li>
              </ul>
              <p className="text-xs text-slate-500">
                {hasActiveDeals ? "All except deals will be permanently deleted. " : ""}
                Assets themselves are not deleted. Linked tasks are kept (unlinked). This cannot be undone.
              </p>
              <div>
                <Label className="text-xs">Type the broker's name to confirm</Label>
                <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={broker.broker_name} className="mt-1" data-testid="delete-confirm-input" />
              </div>
            </div>
            <DialogFooter>
              {multiStep && (
                <Button variant="outline" onClick={() => setStep(1)} data-testid="delete-back-btn">Back</Button>
              )}
              <Button variant="outline" onClick={onClose} data-testid="delete-cancel-btn">Cancel</Button>
              <Button className="bg-red-600 hover:bg-red-700 text-white" disabled={!nameMatches || del.isPending} onClick={() => del.mutate()} data-testid="delete-confirm-btn">
                {del.isPending ? "Deleting…" : "Delete Broker"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}


export default function BrokerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const roles = user?.roles || [user?.role];
  const isAdmin = roles.includes("admin");
  const isSMOnly = roles.includes("sourcing_manager") && !roles.includes("admin") && !roles.includes("relationship_manager");
  const [showActivity, setShowActivity] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showArchivedShares, setShowArchivedShares] = useState(false);

  const { data: broker, isError } = useQuery({ queryKey: ["broker", id], queryFn: async () => (await api.get(`/brokers/${id}`)).data });
  const { data: shares = [] } = useQuery({ queryKey: ["broker-shares", id], queryFn: async () => (await api.get("/brokers/shares", { params: { broker_id: id } })).data });
  const { data: activities = [] } = useQuery({ queryKey: ["broker-activities", id], queryFn: async () => (await api.get(`/brokers/${id}/activities`)).data });
  const { data: commissions = [] } = useQuery({ queryKey: ["broker-commissions", id], queryFn: async () => (await api.get(`/brokers/${id}/commissions`)).data, enabled: !isSMOnly });
  const { data: history = [] } = useQuery({ queryKey: ["broker-history", id], queryFn: async () => (await api.get(`/brokers/${id}/status-history`)).data });
  const { data: offeredAssets = [] } = useQuery({ queryKey: ["broker-offered-assets", id], queryFn: async () => (await api.get(`/offers/by-broker/${id}`)).data });
  const { data: shareStatuses = [] } = useMasterList("broker_share_status", {});
  const { data: relStatuses = [] } = useMasterList("broker_relationship_status", {});

  const ARCHIVED_ASSET_STATUSES = ["Won", "Lost", "Closed", "Expired"];
  const { activeShares, archivedShares } = useMemo(() => ({
    activeShares: shares.filter((s) => !ARCHIVED_ASSET_STATUSES.includes(s.asset_status)),
    archivedShares: shares.filter((s) => ARCHIVED_ASSET_STATUSES.includes(s.asset_status)),
  }), [shares]);

  const shareRow = (s, archived) => (
    <tr key={s.id} className={`border-t border-slate-100 ${archived ? "bg-slate-50" : ""}`} data-testid={`share-row-${s.id}`}>
      <td className={`text-sm font-medium ${archived ? "text-slate-600" : "text-slate-900"}`}>
        {s.asset_name}
        {archived && <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-200 text-slate-600 align-middle" data-testid={`share-archived-label-${s.id}`}>Archived · {s.asset_status}</span>}
      </td>
      <td className="text-sm text-slate-600 font-mono-num">{formatDate(s.shared_at)}</td>
      <td className="text-sm text-slate-600">{s.exclusivity_type}{s.exclusivity_expiry ? ` (→${s.exclusivity_expiry.slice(0, 10)})` : ""}</td>
      <td className="text-sm text-slate-600 font-mono-num">{s.next_followup_date ? formatDate(s.next_followup_date) : "—"}</td>
      <td>
        <Select value={s.share_status_id} onValueChange={(v) => setShareStatus.mutate({ shareId: s.id, sid: v })} disabled={blacklisted}>
          <SelectTrigger className="h-8 w-52 text-xs" data-testid={`share-status-${s.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{shareStatuses.map((st) => <SelectItem key={st.id} value={st.id}>{st.label}</SelectItem>)}</SelectContent>
        </Select>
      </td>
    </tr>
  );

  const setRelStatus = useMutation({
    mutationFn: async (sid) => api.patch(`/brokers/${id}`, { relationship_status_id: sid }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["broker", id] }); qc.invalidateQueries({ queryKey: ["broker-history", id] }); toast.success("Status updated"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const setShareStatus = useMutation({
    mutationFn: async ({ shareId, sid }) => api.patch(`/brokers/shares/${shareId}`, { share_status_id: sid }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["broker-shares", id] }); toast.success("Share status updated"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const scheduleFollowup = useMutation({
    mutationFn: async (dt) => api.patch(`/brokers/${id}`, { next_contact_date: new Date(dt).toISOString() }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["broker", id] }); toast.success("Follow-up scheduled"); },
  });
  const markPaid = useMutation({
    mutationFn: async (cid) => api.patch(`/brokers/commissions/${cid}`, { payment_status: "Paid", payment_date: new Date().toISOString().slice(0, 10) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["broker-commissions", id] }); toast.success("Marked Paid"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  if (isError) return <div className="p-8"><Button variant="ghost" onClick={() => navigate("/brokers")}><ArrowLeft className="h-4 w-4 mr-1.5" />Brokers</Button><div className="text-sm text-red-600 mt-4">Broker not found or access denied.</div></div>;
  if (!broker) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  const blacklisted = broker.relationship_status_label === "Blacklisted";
  const phone = (broker.contact_no || "").replace(/[^0-9]/g, "");

  return (
    <div className="p-6 lg:p-8" data-testid="broker-detail">
      <button onClick={() => navigate("/brokers")} className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" /> Brokers</button>

      {/* Header */}
      <div className="mt-2 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-2xl lg:text-3xl font-bold text-slate-900">{broker.broker_name}</h1>
            <TonalBadge listType="broker_relationship_status" label={broker.relationship_status_label} testid="broker-status-badge" />
            {broker.broker_type_label && <Badge variant="outline" className="text-[10px] border-slate-200 text-slate-600">{broker.broker_type_label}</Badge>}
          </div>
          <div className="text-sm text-slate-500 mt-1">{broker.firm_name || "—"} · <span className="font-mono-num">{broker.broker_code}</span></div>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowEdit(true)} data-testid="edit-broker-btn"><Pencil className="h-3.5 w-3.5 mr-1.5" />Edit</Button>
        {isAdmin && <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" onClick={() => setShowDelete(true)} data-testid="delete-broker-btn"><Trash2 className="h-3.5 w-3.5 mr-1.5" />Delete</Button>}
      </div>

      {blacklisted && (
        <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="blacklist-banner">
          This broker is Blacklisted. New shares are blocked; existing shares are read-only history.
        </div>
      )}

      {/* Quick actions */}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="outline" size="sm" asChild><a href={`tel:${phone}`} data-testid="qa-call"><Phone className="h-3.5 w-3.5 mr-1.5" />Call</a></Button>
        <Button variant="outline" size="sm" asChild><a href={`https://wa.me/${phone}`} target="_blank" rel="noreferrer" data-testid="qa-whatsapp"><MessageCircle className="h-3.5 w-3.5 mr-1.5" />WhatsApp</a></Button>
        <Button variant="outline" size="sm" onClick={() => { const dt = prompt("Next contact date (YYYY-MM-DD):"); if (dt) scheduleFollowup.mutate(dt); }} data-testid="qa-schedule"><CalendarPlus className="h-3.5 w-3.5 mr-1.5" />Schedule Follow Up</Button>
        <Button variant="outline" size="sm" onClick={() => setShowActivity(true)} data-testid="qa-log-activity"><NotebookPen className="h-3.5 w-3.5 mr-1.5" />Log Activity</Button>
        <Button variant="outline" size="sm" onClick={() => navigate(`/meetings/new?broker=${id}`)} data-testid="qa-log-meeting"><CalendarClock className="h-3.5 w-3.5 mr-1.5" />Log Meeting</Button>
        <Button size="sm" className="bg-slate-900 hover:bg-slate-800" disabled={blacklisted} onClick={() => setShowShare(true)} data-testid="qa-share-asset"><Share2 className="h-3.5 w-3.5 mr-1.5" />Share Asset</Button>
        <QuickNoteButton recordType="Broker" recordId={id} recordName={broker.broker_name} />
      </div>

      {/* Profile + relationship status */}
      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-md p-4 text-sm space-y-1.5">
          <div className="label-eyebrow !text-slate-500 mb-1">Contact</div>
          <div>Phone: <span className="font-mono-num">{broker.contact_no || "—"}</span></div>
          <div>Alt: <span className="font-mono-num">{broker.alternate_no || "—"}</span></div>
          <div>Email: {broker.email || "—"}</div>
          <div>Owner: {broker.relationship_owner_name || "—"}</div>
          <div>Onboarded: {broker.onboarded_date ? formatDate(broker.onboarded_date) : "—"}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4 text-sm space-y-1.5">
          <div className="label-eyebrow !text-slate-500 mb-1">Commission Defaults</div>
          <div>Split: {broker.commission_split_type_label || "—"}</div>
          <div>Rate: <span className="font-mono-num">{broker.commission_split_rate ?? "—"}</span></div>
          <div className="label-eyebrow !text-slate-500 mb-1 mt-2">Coverage / Specialization</div>
          <div>{(broker.coverage_city_ids || []).length} cities · {(broker.specialization_category_ids || []).length} categories</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4 text-sm">
          <div className="label-eyebrow !text-slate-500 mb-2">Relationship Status</div>
          <Select value={broker.relationship_status_id || ""} onValueChange={(v) => setRelStatus.mutate(v)}>
            <SelectTrigger data-testid="rel-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {relStatuses.map((s) => (
                <SelectItem key={s.id} value={s.id} disabled={s.label === "Blacklisted" && !isAdmin}>
                  {s.label}{s.label === "Blacklisted" && !isAdmin ? " (admin only)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {broker.short_notes && <div className="mt-3 text-xs text-slate-500">{broker.short_notes}</div>}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="shares" className="mt-6">
        <TabsList data-testid="broker-tabs">
          <TabsTrigger value="shares" data-testid="tab-shares">Shared Assets ({shares.length})</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Activity Log ({activities.length})</TabsTrigger>
          {!isSMOnly && <TabsTrigger value="commission" data-testid="tab-commission">Commission ({commissions.length})</TabsTrigger>}
          <TabsTrigger value="offered" data-testid="tab-broker-offered">Assets Offered ({offeredAssets.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Status History</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks</TabsTrigger>
          <TabsTrigger value="meetings" data-testid="tab-meetings">Meetings</TabsTrigger>
        </TabsList>

        <TabsContent value="shares">
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="data-table w-full">
              <thead><tr><th className="label-eyebrow !text-slate-600">Asset</th><th className="label-eyebrow !text-slate-600">Shared</th><th className="label-eyebrow !text-slate-600">Exclusivity</th><th className="label-eyebrow !text-slate-600">Next Follow-up</th><th className="label-eyebrow !text-slate-600">Status</th></tr></thead>
              <tbody>
                {shares.length === 0 ? <tr><td colSpan={5} className="text-center py-8 text-sm text-slate-500">No shares yet.</td></tr> : (
                  <>
                    {activeShares.map((s) => shareRow(s, false))}
                    {activeShares.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-sm text-slate-500" data-testid="shares-no-active">No active shared assets.</td></tr>}
                    {archivedShares.length > 0 && (
                      <tr className="border-t border-slate-100">
                        <td colSpan={5} className="p-0">
                          <button onClick={() => setShowArchivedShares((v) => !v)} className="w-full flex items-center justify-center gap-1.5 py-3 min-h-[48px] text-xs text-slate-500 hover:text-slate-700" data-testid="shares-archived-toggle">
                            {showArchivedShares ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                            {showArchivedShares ? "Hide" : "Show"} archived shared assets ({archivedShares.length})
                          </button>
                        </td>
                      </tr>
                    )}
                    {showArchivedShares && archivedShares.map((s) => shareRow(s, true))}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="activity">
          <div className="space-y-2">
            {activities.length === 0 ? <div className="text-sm text-slate-500 py-6 text-center bg-white border border-slate-200 rounded-md">No activity logged.</div> : activities.map((a) => (
              <div key={a.id} className="bg-white border border-slate-200 rounded-md p-3" data-testid={`activity-row-${a.id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-900">{a.activity_type_label}{a.outcome_label ? ` · ${a.outcome_label}` : ""}</div>
                  <div className="text-[11px] text-slate-400 font-mono-num">{formatDate(a.logged_at)} · {a.logged_by_name}</div>
                </div>
                {a.comments && <div className="text-sm text-slate-600 mt-1">{a.comments}</div>}
                {a.next_followup_date && <div className="text-[11px] text-violet-600 mt-1">Next: {formatDate(a.next_followup_date)}</div>}
              </div>
            ))}
          </div>
        </TabsContent>

        {!isSMOnly && (
        <TabsContent value="commission">
          <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <table className="data-table w-full">
              <thead><tr><th className="label-eyebrow !text-slate-600">Deal</th><th className="label-eyebrow !text-slate-600">Split</th><th className="label-eyebrow !text-slate-600 text-right">Amount</th><th className="label-eyebrow !text-slate-600">Payment</th><th className="label-eyebrow !text-slate-600"></th></tr></thead>
              <tbody>
                {commissions.length === 0 ? <tr><td colSpan={5} className="text-center py-8 text-sm text-slate-500">No commissions yet.</td></tr> : commissions.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100" data-testid={`commission-row-${c.id}`}>
                    <td className="text-sm text-slate-600 font-mono-num">{c.deal_id.slice(0, 8)}</td>
                    <td className="text-sm text-slate-600">{c.commission_split_type_label || "—"} {c.commission_split_rate != null ? `(${c.commission_split_rate})` : ""}</td>
                    <td className="text-sm text-right font-mono-num font-semibold">{formatINR(c.commission_amount)}</td>
                    <td><Badge variant="outline" className={`text-[10px] ${c.payment_status === "Paid" ? "border-emerald-200 text-emerald-700" : "border-amber-200 text-amber-700"}`}>{c.payment_status}{c.payment_date ? ` · ${c.payment_date}` : ""}</Badge></td>
                    <td className="text-right">{c.payment_status !== "Paid" && isAdmin && <Button size="sm" variant="outline" onClick={() => markPaid.mutate(c.id)} data-testid={`mark-paid-${c.id}`}><BadgeCheck className="h-3.5 w-3.5 mr-1" />Mark Paid</Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
        )}

        <TabsContent value="offered">
          <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="broker-offered-assets">
            {offeredAssets.length === 0 ? (
              <div className="text-sm text-slate-500 py-6 text-center">No assets offered to this broker yet.</div>
            ) : (
              <table className="data-table w-full">
                <thead><tr>
                  <th className="label-eyebrow !text-slate-600">Asset</th>
                  <th className="label-eyebrow !text-slate-600">Category</th>
                  <th className="label-eyebrow !text-slate-600">City</th>
                  <th className="label-eyebrow !text-slate-600">Offered</th>
                  <th className="label-eyebrow !text-slate-600">Status</th>
                </tr></thead>
                <tbody>
                  {offeredAssets.map((o) => (
                    <tr key={o.offer_id} className="border-t border-slate-100" data-testid={`broker-offer-row-${o.offer_id}`}>
                      <td className="text-sm font-medium text-slate-900">{o.asset_name}<div className="text-[10px] text-slate-400 font-mono-num">{o.asset_code}</div></td>
                      <td className="text-sm text-slate-600">{o.asset_category_label || "—"}</td>
                      <td className="text-sm text-slate-600">{o.city_label || "—"}</td>
                      <td className="text-sm text-slate-600 font-mono-num">{formatDate(o.offered_date)}</td>
                      <td><TonalBadge listType="offer_status" label={o.offer_status_label} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="history">
          <div className="bg-white border border-slate-200 rounded-md p-4 space-y-2">
            {history.length === 0 ? <div className="text-sm text-slate-500">No status changes.</div> : history.map((h) => (
              <div key={h.id} className="flex items-center justify-between text-sm border-b border-slate-100 pb-2" data-testid={`history-row-${h.id}`}>
                <div>{h.old_status_label || "—"} → <span className="font-semibold">{h.new_status_label}</span></div>
                <div className="text-[11px] text-slate-400 font-mono-num">{formatDate(h.changed_at)} · {h.changed_by_name}</div>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="tasks">
          <RecordTasksTab recordType="Broker" recordId={id} recordName={broker.broker_name} />
        </TabsContent>
        <TabsContent value="meetings">
          <BrokerMeetingsTab brokerId={id} />
        </TabsContent>
      </Tabs>

      <LogActivityDialog open={showActivity} onClose={() => setShowActivity(false)} brokerId={id} shares={shares} />
      {showEdit && <EditBrokerDialog open={showEdit} onClose={() => setShowEdit(false)} broker={broker} />}
      {showDelete && <DeleteBrokerDialog open={showDelete} onClose={() => setShowDelete(false)} broker={broker} onDeleted={() => navigate("/brokers/retail")} />}
      <ShareAssetDialog open={showShare} onClose={() => setShowShare(false)} brokerId={id} />
    </div>
  );
}
